import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, of, switchMap, tap } from 'rxjs';

import { AccountStateService } from './account-state.service';
import { AuthApiService } from './auth-api.service';
import { StorageService } from './storage.service';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import {
  AuthResponse,
  ExternalAuthCallbackPayload,
  ExternalAuthProvider,
  ExternalAuthProviderOption,
  ExternalAuthRegistrationPayload,
  LinkedExternalProvider,
  RegisterOwnerRequest,
  User,
  UserRole
} from '@app/core/models';

@Injectable({ providedIn: 'root' })
export class AuthStoreService {
  private readonly authApi = inject(AuthApiService);
  private readonly accountState = inject(AccountStateService);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);
  private readonly analytics = inject(AnalyticsService);

  private readonly userState = signal<User | null>(null);
  private readonly tokenState = signal<string | null>(null);
  private readonly refreshTokenState = signal<string | null>(null);
  private readonly linkedProvidersState = signal<LinkedExternalProvider[]>([]);
  private readonly externalProvidersState = signal<ExternalAuthProviderOption[]>([]);
  private accountStateEpoch = 0;

  readonly user = computed(() => this.userState());
  readonly token = computed(() => this.tokenState());
  readonly refreshToken = computed(() => this.refreshTokenState());
  readonly linkedProviders = computed(() => this.linkedProvidersState());
  readonly externalProviders = computed(() => this.externalProvidersState());
  readonly isAuthenticated = computed(() => !!this.tokenState() && !!this.userState());
  readonly role = computed<UserRole | null>(() => this.userState()?.role ?? null);
  readonly mustChangePassword = computed(() => this.userState()?.mustChangePassword === true);
  readonly homeRoute = computed(() => {
    const role = this.role();

    if (role === 'partner') {
      return '/partner/dashboard';
    }

    if (role === 'admin') {
      return '/admin/dashboard';
    }

    return '/dashboard';
  });

  async restoreSession(): Promise<void> {
    const token = this.storage.getToken();
    const refreshToken = this.storage.getRefreshToken();

    if (!token) {
      this.clearSession();
      return;
    }

    this.tokenState.set(token);
    this.refreshTokenState.set(refreshToken);

    await firstValueFrom(
      this.authApi.me().pipe(
        tap((user) => {
          const previousUser = this.userState();
          const switchedUser = !!previousUser && previousUser.id !== user.id;
          const changedSession = !!previousUser && (switchedUser || previousUser.role !== user.role ||
            previousUser.isImpersonating !== user.isImpersonating);
          if (changedSession) {
            void this.analytics.suspend();
            this.analytics.resetIdentity();
          }
          this.userState.set(user);
          this.syncAnalyticsIdentity(user, changedSession);
          if (user.role === 'admin') {
            this.storage.clearImpersonationSession();
          }
        }),
        switchMap(() => this.loadAccountState()),
        map(() => void 0),
        catchError(() => {
          this.clearSession();
          return of(void 0);
        })
      )
    );
  }

  login(email: string, password: string) {
    return this.authApi.login({ email, password }).pipe(
      tap((response) => this.applySession(response)),
      switchMap((response) => this.loadAccountState().pipe(map(() => response)))
    );
  }

  registerOwner(payload: RegisterOwnerRequest, idempotencyKey?: string) {
    return this.authApi.register(payload, idempotencyKey).pipe(
      tap((response) => this.applySession(response)),
      switchMap((response) => this.loadAccountState().pipe(map(() => response)))
    );
  }

  loadExternalProviders() {
    return this.authApi.getExternalProviders().pipe(
      tap((providers) => this.externalProvidersState.set(providers))
    );
  }

  startExternalLogin(provider: ExternalAuthProvider, role: 'owner' | 'partner'): void {
    this.authApi.startExternalLogin(provider, role);
  }

  completeExternalAuth(payload: ExternalAuthCallbackPayload) {
    return this.authApi.completeExternalAuth(payload).pipe(
      tap((response) => this.applySession(response)),
      switchMap((response) =>
        this.loadAccountState().pipe(
          map(() => response.user)
        )
      )
    );
  }

  completeExternalRegistration(payload: ExternalAuthRegistrationPayload) {
    return this.authApi.completeExternalRegistration(payload).pipe(
      tap((response) => this.applySession(response)),
      switchMap((response) =>
        this.loadAccountState().pipe(
          map(() => response.user)
        )
      )
    );
  }

  loadLinkedProviders() {
    return this.authApi.getLinkedProviders().pipe(
      tap((providers) => this.linkedProvidersState.set(providers))
    );
  }

  loadAccountState() {
    const epoch = this.accountStateEpoch;
    this.accountState.setLoading(true);

    return this.authApi.getAccountState().pipe(
      tap((state) => {
        if (epoch !== this.accountStateEpoch) return;
        this.accountState.setState(state);
        this.accountState.setLoading(false);
      }),
      catchError((error) => {
        if (epoch === this.accountStateEpoch) this.accountState.setLoading(false);
        return of(null);
      })
    );
  }

  logout(navigate = true): void {
    const refreshToken = this.refreshTokenState() ?? this.storage.getRefreshToken();
    if (refreshToken) {
      this.authApi.logout(refreshToken).pipe(catchError(() => of(void 0))).subscribe();
    }

    this.clearSession();

    if (navigate) {
      void this.router.navigate(['/login']);
    }
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = this.role();
    return !!currentRole && roles.includes(currentRole);
  }

  updateLocalUser(patch: Partial<User>): void {
    const currentUser = this.userState();
    if (!currentUser) {
      return;
    }

    this.userState.set({
      ...currentUser,
      ...patch
    });
  }

  getHomeRoute(): string {
    return this.homeRoute();
  }

  getPostAuthRoute(): string {
    if (this.mustChangePassword()) {
      return '/change-password';
    }

    return this.accountState.getPostAuthRoute(this.homeRoute());
  }

  applySession(response: AuthResponse): void {
    const previousUser = this.userState();
    const switchedUser = !!previousUser && previousUser.id !== response.user.id;
    const changedSession = !!previousUser && (switchedUser || previousUser.role !== response.user.role ||
      previousUser.isImpersonating !== response.user.isImpersonating);
    if (changedSession) {
      void this.analytics.suspend();
      this.analytics.resetIdentity();
    }
    if (this.userState()?.id !== response.user.id) {
      this.accountStateEpoch++;
      this.storage.clearSelectedPropertyId();
      this.storage.clearSelectedClientAccountId();
      this.accountState.clear();
    }
    this.storage.setToken(response.token);
    this.tokenState.set(response.token);

    if (response.refreshToken) {
      this.storage.setRefreshToken(response.refreshToken);
      this.refreshTokenState.set(response.refreshToken);
    }

    this.userState.set(response.user);
    this.syncAnalyticsIdentity(response.user, changedSession);
    this.linkedProvidersState.set(response.linkedProviders ?? []);
  }

  private syncAnalyticsIdentity(user: User, identityWasReset = false): void {
    if (user.role === 'owner' && user.isImpersonating !== true && this.storage.getImpersonationSession() === null) {
      this.analytics.identify(user.id);
    } else if (!identityWasReset) {
      this.analytics.resetIdentity();
    }
  }

  private clearSession(): void {
    void this.analytics.suspend();
    this.analytics.resetIdentity();
    this.accountStateEpoch++;
    const currentUserId = this.userState()?.id;

    this.storage.clearToken();
    this.storage.clearRefreshToken();
    this.storage.clearSelectedPropertyId();
    this.storage.clearSelectedClientAccountId();
    this.storage.clearImpersonationSession();
    if (currentUserId) {
      this.storage.clearOwnerProfileDraft(currentUserId);
    }
    this.tokenState.set(null);
    this.refreshTokenState.set(null);
    this.userState.set(null);
    this.linkedProvidersState.set([]);
    this.accountState.clear();
  }
}
